import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { User } from "@/types/user.ts";
import type { Rsp } from "@repo/types";
import { UserAPI } from "@repo/api/user";
import { Badge, CollectableBadge } from "@/pages/user/badges/-components";
import { Img } from "react-image";
import ErrorPage from "@/pages/-error";
import { useDocumentTitle } from "@/hooks/useDocumentTitle.ts";

export const Route = createFileRoute("/user/badges/")({
  component: BadgeIndex,
});


export default function BadgeIndex() {
  const [data, setData] = useState<User.Badge[]>([]);
  const [collectableBadge, setCollectableBadge] = useState<User.Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Rsp<null>>({
    status: 0,
    msg: "",
    data: null,
    request_id: "",
    timestamp: 0,
  });

  useDocumentTitle("我的徽章");

  const fetchData = () =>
    // 使用 Promise.all() 并行处理多个请求
    Promise.all([UserAPI.getBadgeList(), UserAPI.getCollectableBadgeList()])
      .then(([badgeResponse, collectableBadgeResponse]) => {
        // 分别处理每个请求的结果
        setData(badgeResponse.data || []);
        setCollectableBadge(collectableBadgeResponse.data || []);
      })
      .catch((err) => {
        // 处理任何一个请求出错的情况
        setError(err);
      })
      .finally(() => {
        // 不管成功还是失败，最后都会执行，关闭 loading 状态
        setLoading(false);
      });

  useEffect(() => {
    setLoading(true);
    fetchData().then();
  }, []);

  if (loading) {
    return (
      <div className={"flex min-h-screen items-center justify-center"}>
        <div>Loading...</div>
      </div>
    );
  }

  if (error.status !== 0) return <ErrorPage error={error} />;

  return (
    <div
      className={
        "flex min-h-screen flex-col gap-4 bg-gradient-to-b from-[#ffa072] via-[#ff616a] to-[#FFFF] p-4"
      }
    >
      <div className={"rounded-xl bg-white p-4 shadow-2xl"}>
        <div className={"mb-2 text-xl font-bold text-gray-900"}>我的成就</div>
        {data.length === 0 ? (
          <div className={"flex min-h-screen flex-col items-center justify-center opacity-40"}>
            <Img alt={"empty"} src={"/img/faill@1.0x.png"} className="h-32 w-32" />
            <div className={"text-sm"}>啊呀，您还没有获得任何成就徽章喔</div>
            <div className={"text-sm"}>快快加油吧~</div>
          </div>
        ) : (
          <div className={"grid grid-cols-3 gap-2 md:grid-cols-4 lg:grid-cols-6"}>
            {data.map((badge) => {
              return (
                <Badge
                  key={badge.id}
                  id={badge.id}
                  title={badge.name}
                  img={badge.image}
                  gotAt={badge.unlock_at}
                  collectable={badge.collectable}
                  quality={badge.quality}
                />
              );
            })}
            {collectableBadge.map((badge) => {
              return (
                <CollectableBadge
                  key={badge.id}
                  id={badge.id}
                  title={badge.name}
                  img={badge.image}
                  refresh={fetchData}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
